import { scaleTime } from 'd3'

const ONE_HOUR_MS = 60 * 60 * 1000

type TimestampedRecord = { timestampMs: number; [key: string]: any }

/**
 * Generates ~targetCount evenly spaced tick timestamps for a time-axis chart.
 * Uses d3's scaleTime().ticks() which picks "nice" human-readable boundaries
 * (e.g. every 3 hours, every day) close to the requested count — the actual
 * number of ticks may be slightly more or fewer than targetCount.
 */
export const getChartTicks = (data: TimestampedRecord[], targetCount = 6) => {
  if (data.length === 0) {
    return undefined
  }

  const min = data[0].timestampMs
  const max = data[data.length - 1].timestampMs
  const scale = scaleTime().domain([new Date(min), new Date(max)])
  return scale.ticks(targetCount).map(d => d.getTime())
}

/**
 *  Insert 2 synthetic 'null' datapoints for each gap > gapMs:
 *  one immediately after the previous ts, and one immediately before the next ts
 *  (Assume timestamps are in ms, sorted ascending)
 */
export const addBoundaryNullsForGaps = <T extends TimestampedRecord>(
  data: T[],
  valueKeys: string[],
  gapMs = ONE_HOUR_MS
): T[] => {
  if (!data || data.length === 0) {
    return data
  }

  const filled: T[] = [data[0]]
  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1]
    const curr = data[i]
    const prevTs = prev.timestampMs
    const currTs = curr.timestampMs

    if (currTs - prevTs > gapMs) {
      // First datapoint: prevTs + 1ms
      const firstNull: Record<string, any> = { timestampMs: prevTs + 1 }
      valueKeys.forEach(k => (firstNull[k] = null))
      filled.push(firstNull as T)

      // Second datapoint: currTs - 1ms
      const secondNull: Record<string, any> = { timestampMs: currTs - 1 }
      valueKeys.forEach(k => (secondNull[k] = null))
      filled.push(secondNull as T)
    }
    filled.push(curr)
  }
  return filled
}

// SAV data has different bucket sizes per interval (unlike the position-metrics
// history chart, which has a fixed ~1h cadence — see POSITION_METRICS_GAP_THRESHOLD_MS).
// A fixed 1h threshold would show false gaps on longer intervals where buckets are
// naturally 4h or 12h apart. Each threshold is 2× the bucket size so a gap only
// appears when at least one full bucket of data is missing.
const SAV_GAP_THRESHOLDS: Record<string, number> = {
  '24h': 2 * 15 * 60 * 1000, // 30min (buckets are 15min apart)
  '7d': 2 * 60 * 60 * 1000, // 2h (buckets are 1h apart)
  '30d': 2 * 4 * 60 * 60 * 1000, // 8h (buckets are 4h apart)
  '3m': 2 * 12 * 60 * 60 * 1000, // 24h (buckets are 12h apart)
}

export const getGapThresholdForSavInterval = (interval: string): number =>
  SAV_GAP_THRESHOLDS[interval] ?? ONE_HOUR_MS

// Position-metrics history runs on the producer's ~hourly grid, but the cadence
// jitters — normal consecutive points land ≈60–75min apart. The default 1h
// threshold therefore false-flags almost every normal interval as a gap (~half of
// them, since the grid averages ~62min). 1.5h sits comfortably above that jitter
// but below a genuinely skipped datapoint (a missing point shows up as ≈95min+
// between neighbours), so only real gaps render.
export const POSITION_METRICS_GAP_THRESHOLD_MS = 90 * 60 * 1000
